import { useMemo, useState } from 'react';
import { Janela, Selo } from '../../../common/interface';
import { quando } from '../../../../utils/formato';
import { dataDoDia, moeda } from '../../formato';
import { OrigemPix, origemFatura, usePixCobranca } from '../../usePixCobranca';
import type { FaturaCte } from './types';

interface Props {
  /** `null` fecha o diálogo. */
  fatura: FaturaCte | null;
  onFechar: () => void;
  /** Qualquer desfecho que mude a fatura — a lista precisa recarregar. */
  onDesfecho: () => void;
}

/**
 * Pagamento de fatura por Pix.
 *
 * Reaproveita `usePixCobranca`, o mesmo mecanismo da recarga — cobrança,
 * acompanhamento e desfechos são idênticos; só muda quem cria e o texto de
 * contexto. É a generalização que o app fez em `pix_origem.dart`.
 */
export function PixFaturaSheet({ fatura, onFechar, onDesfecho }: Props) {
  // `useMemo` pelo id: sem ele, cada render criaria uma origem nova e o efeito
  // geraria outra cobrança.
  const origem = useMemo(() => (fatura ? origemFatura(fatura.id) : null), [fatura?.id]);
  return (
    <PixDaCobranca
      origem={origem}
      contexto={fatura ? `Fatura #${fatura.id} · vence ${dataDoDia(fatura.dtVencimento)}` : undefined}
      valor={fatura?.valor ?? null}
      oQueBaixa="fatura"
      onFechar={onFechar}
      onDesfecho={onDesfecho}
    />
  );
}

/**
 * A janela do Pix, para o que se paga com ele: a fatura de frete ou a
 * mensalidade (0105). Só mudam a origem, o contexto e a palavra do desfecho.
 */
export function PixDaCobranca({ origem, contexto, valor, oQueBaixa, onFechar, onDesfecho }: {
  origem: OrigemPix | null;
  contexto?: string;
  valor: number | null;
  oQueBaixa: 'fatura' | 'mensalidade';
  onFechar: () => void;
  onDesfecho: () => void;
}) {
  const [copiado, setCopiado] = useState(false);
  const { cobranca, statusEfetivo, criando, erro, tetoAtingido, restanteMs, consultarAgora } =
    usePixCobranca(origem, onDesfecho);
  const A = oQueBaixa === 'fatura' ? 'A fatura' : 'A mensalidade';
  const a = oQueBaixa === 'fatura' ? 'a fatura' : 'a mensalidade';

  const minutos = Math.floor(restanteMs / 60_000);
  const segundos = Math.floor((restanteMs % 60_000) / 1000);

  function copiar() {
    if (!cobranca?.pixCopiaECola) return;
    navigator.clipboard?.writeText(cobranca.pixCopiaECola);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  const pago = statusEfetivo === 'CONCLUIDA';
  const terminal = pago || statusEfetivo === 'CONCLUIDA_DIVERGENTE' || statusEfetivo === 'EXPIRADA'
    || statusEfetivo === 'CANCELADA' || statusEfetivo === 'ERRO';

  return (
    <Janela
      aberta={origem !== null}
      titulo="Pagamento por Pix"
      contexto={contexto}
      apoio={statusEfetivo === 'ATIVA' && !tetoAtingido
        ? `Acompanhando o pagamento por mais ${minutos}:${String(segundos).padStart(2, '0')}.`
        : null}
      erro={erro}
      onFechar={onFechar}
      acoes={erro || terminal ? (
        <button type="button" className={`co-btn ${pago ? 'co-btn-primario' : 'co-btn-secundario'}`} onClick={onFechar}>
          Fechar
        </button>
      ) : undefined}
    >
      {valor !== null && <div className="co-pix-valor">{moeda(valor)}</div>}

      {(criando || statusEfetivo === 'CRIANDO') && !erro && <p className="co-apoio" role="status">Gerando o código Pix…</p>}

      {!erro && statusEfetivo === 'ATIVA' && cobranca && (
        <div className="co-pilha">
          <Selo papel="atencao">Aguardando pagamento</Selo>

          {tetoAtingido && (
            <p className="co-nota co-nota-atencao" role="status">
              Ainda não recebemos a confirmação. Se você já pagou, {a} baixa sozinha; toque em "Já paguei" para
              verificar agora.
            </p>
          )}

          {/* O QR vem do base64: `qrCodeUrl` aponta para a superfície mobile e
              exige x-api-key, então num <img> falharia em silêncio. */}
          {cobranca.qrCodePngBase64 && (
            <div className="co-pix-qr">
              <img src={`data:image/png;base64,${cobranca.qrCodePngBase64}`} alt="QR Code do Pix" />
              <p className="co-apoio">Abra o app do seu banco, escolha Pix e aponte a câmera.</p>
            </div>
          )}

          {cobranca.pixCopiaECola && (
            <div>
              <span className="co-rotulo">Pix copia e cola</span>
              <p className="co-pix-codigo">{cobranca.pixCopiaECola}</p>
            </div>
          )}

          <button type="button" className="co-btn co-btn-primario co-btn-largo" onClick={copiar}>
            {copiado ? 'Código copiado' : 'Copiar código Pix'}
          </button>
          {/* Não é conveniência: o app do banco não devolve o cooperado a esta
              tela, e ele precisa de um jeito de perguntar agora. */}
          <button type="button" className="co-btn co-btn-secundario co-btn-largo" onClick={consultarAgora}>
            Já paguei / Atualizar
          </button>
        </div>
      )}

      {!erro && statusEfetivo === 'CONCLUIDA' && (
        <Desfecho
          titulo="Pagamento confirmado"
          texto={cobranca?.pagoEm ? `Recebido em ${quando(cobranca.pagoEm)}. ${A} foi baixada.` : `${A} foi baixada.`}
        />
      )}

      {!erro && statusEfetivo === 'CONCLUIDA_DIVERGENTE' && (
        // O estado perigoso: o dinheiro saiu e a fatura NÃO baixou, então o
        // cooperado continua bloqueado. A copy reconhece o recebimento e
        // impede a duplicação — dizer "não identificado" o faria pagar de
        // novo; dizer "pago!" o faria não entender o bloqueio.
        <Desfecho
          titulo="Recebemos seu pagamento"
          texto={`Mas o valor não confere com ${a}. A cooperativa já foi avisada e vai regularizar — não pague de novo.`}
        />
      )}

      {!erro && statusEfetivo === 'EXPIRADA' && (
        <Desfecho titulo="Este código Pix expirou" texto="Feche e toque em pagar de novo para gerar outro." />
      )}

      {!erro && (statusEfetivo === 'CANCELADA' || statusEfetivo === 'ERRO') && (
        <Desfecho
          titulo={statusEfetivo === 'CANCELADA' ? 'Esta cobrança foi cancelada' : 'Não foi possível gerar o Pix'}
          texto="Feche e tente de novo."
        />
      )}
    </Janela>
  );
}

function Desfecho({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div className="co-desfecho" role="status">
      <strong className="co-titulo-cartao">{titulo}</strong>
      <p className="co-apoio">{texto}</p>
    </div>
  );
}
